import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Crown, Flame } from "lucide-react";
import { DiscordBadge } from "@/components/DiscordBadge";

/**
 * Prediction streak leaderboard — ranked by current streak, then best, then hit rate.
 */
export default function PredictionStreakLeaderboard() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/predictions/streaks").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  if (!rows) return <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Loading…</div>;
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-[#232330] p-8 text-center text-gray-500 font-mono text-xs uppercase tracking-widest" data-testid="streaks-empty">
        No settled predictions yet. Be the first oracle.
      </div>
    );
  }

  return (
    <div className="border border-[#232330] bg-[#101015]" data-testid="streaks-leaderboard">
      <div className="p-4 border-b border-[#232330] flex items-center gap-3">
        <Flame className="w-4 h-4 text-[#F4D03F]" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">/ PREDICTION STREAKS</div>
          <div className="font-heading font-black uppercase tracking-tight">This season's oracles</div>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#232330]">
            <th className="px-4 py-2 w-10">#</th>
            <th className="px-4 py-2">Player</th>
            <th className="px-4 py-2 text-right">Current</th>
            <th className="px-4 py-2 text-right">Best</th>
            <th className="px-4 py-2 text-right">Hit rate</th>
            <th className="px-4 py-2 text-right">Record</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.user_id} className="border-b border-[#232330]" data-testid={`streak-row-${r.user_id}`}>
              <td className="px-4 py-2 font-mono text-gray-500 tabular-nums">
                {i === 0 ? <Crown className="w-4 h-4 text-[#F4D03F]" /> : String(i + 1).padStart(2, "0")}
              </td>
              <td className="px-4 py-2 font-heading font-bold uppercase tracking-tight">
                <span className="inline-flex items-center gap-1.5">
                  {r.name}
                  {r.discord_username && <DiscordBadge handle={r.discord_username} size={10} testid={`streak-discord-${r.user_id}`} />}
                </span>
              </td>
              <td className="px-4 py-2 text-right font-mono font-bold text-[#F4D03F] tabular-nums">
                {r.current_streak > 0 ? `🔥${r.current_streak}` : "—"}
              </td>
              <td className="px-4 py-2 text-right font-mono tabular-nums">{r.best_streak}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums">{Math.round(r.hit_rate * 100)}%</td>
              <td className="px-4 py-2 text-right font-mono text-xs text-gray-400 tabular-nums">{r.hits}/{r.settled}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
